import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import {
  Surface,
  ToggleRow,
  chipStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { ALL_LOCALES, type Journey, type Locale } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n, LOCALE_NATIVE_NAMES } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { journeysRepoFor } from '../lib/repositories/journeys-repo.js';
import { logger } from '../lib/logger.js';
import {
  buildJourneyUpdate,
  formFromJourney,
  type JourneyForm,
  type StepForm,
} from '../lib/journey-form.js';

/**
 * /marketing/journeys — operator surface for the lifecycle marketing-automation
 * engine (growth-engine Phase 1). The engine + three journeys (win-back /
 * welcome / trial-nudge) ship SEEDED-but-inactive; this page is what lets an
 * operator turn them on and tune them.
 *
 * Minimal-usable slice: ENABLE + TUNE the seeded journeys, not a from-scratch
 * composer. Per card: name, a plain-language trigger summary, the send cadence,
 * a step count, and the enable/disable `ToggleRow` (the core action — flipping
 * it on is what makes the engine deliver value). "Edit steps" expands an editor
 * for each step's per-locale title/body + deep link + timing, the weekly
 * frequency cap, exit-on-return, and (for a segment_scan journey) the audience
 * criteria. Creating brand-new journeys is a documented follow-up.
 *
 * Premium-logical / no-dead-ends: every state is explicit — sign-in, loading
 * skeleton, load-error, empty, idle, saving, saved, validation-error. All copy
 * resolves via `dashboard.journeys.*`; writes go through `journeysRepoFor`
 * (owner/manager, rules-gated). Validation is the pure `buildJourneyUpdate`
 * (unit-tested), so the editor is a thin rendering layer over it.
 */

export function MarketingJourneysPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const sub = useMemo(() => {
    if (!tenantId) return null;
    const repo = journeysRepoFor(tenantId);
    return (cb: (vs: Journey[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const {
    data: journeys,
    loading,
    error,
  } = useTrackedSnapshot<Journey[]>(['journeys', tenantId], sub);

  if (!tenantId) {
    return (
      <Screen
        title={t.t('dashboard.journeys.signInTitle')}
        body={t.t('dashboard.journeys.signInBody')}
        cta={{ label: t.t('dashboard.journeys.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = (journeys ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.journeys.title')}
          </h1>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {loading
              ? t.t('dashboard.journeys.loading')
              : t.t(
                  list.length === 1
                    ? 'dashboard.journeys.countOne'
                    : 'dashboard.journeys.countOther',
                  { count: list.length },
                )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/marketing" style={ghostLinkStyle}>
            {t.t('dashboard.journeys.backToMarketing')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.journeys.backToQueue')}
          </Link>
        </div>
      </header>

      <p
        style={{
          margin: '0 0 var(--space-5) 0',
          maxWidth: 760,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t.t('dashboard.journeys.intro')}
      </p>

      <Surface as="section">
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.journeys.listHeading')}
        </h2>
        <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}>
          {loading ? (
            <SkeletonRows />
          ) : error ? (
            <InlineNote tone="error">
              {t.t('dashboard.journeys.loadError', { error: String(error.message ?? error) })}
            </InlineNote>
          ) : list.length === 0 ? (
            <InlineNote>{t.t('dashboard.journeys.empty')}</InlineNote>
          ) : (
            list.map((journey) => (
              <JourneyCard key={journey.id} tenantId={tenantId} journey={journey} />
            ))
          )}
        </div>
      </Surface>
    </main>
  );
}

// ── card ─────────────────────────────────────────────────────────────────────

function JourneyCard({
  tenantId,
  journey,
}: {
  readonly tenantId: string;
  readonly journey: Journey;
}) {
  const t = useI18n();
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  async function setActive(next: boolean) {
    setToggling(true);
    setToggleError(null);
    try {
      await journeysRepoFor(tenantId).update(journey.id, {
        active: next,
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      logger.error('journey toggle failed', { error: String(e), id: journey.id });
      setToggleError(t.t('dashboard.journeys.card.toggleError'));
    } finally {
      setToggling(false);
    }
  }

  const cadence = journey.steps.map((s) => s.afterDays).join(', ');

  return (
    <article
      style={{
        border: journey.active ? '1px solid rgba(13,122,79,0.32)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        background: journey.active ? 'rgba(13,122,79,0.03)' : 'var(--color-surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <p style={{ margin: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>{journey.name}</p>
            <span
              style={{
                padding: '2px 10px',
                borderRadius: 'var(--radius-pill)',
                fontSize: 11,
                fontWeight: 600,
                background: journey.active ? 'rgba(13,122,79,0.12)' : 'var(--color-border)',
                color: journey.active ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
              }}
            >
              {journey.active
                ? t.t('dashboard.journeys.card.statusActive')
                : t.t('dashboard.journeys.card.statusInactive')}
            </span>
          </div>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {triggerSummary(journey, t)}
          </p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t(
              journey.steps.length === 1
                ? 'dashboard.journeys.card.stepsOne'
                : 'dashboard.journeys.card.stepsOther',
              { count: journey.steps.length },
            )}
            {' · '}
            {t.t('dashboard.journeys.card.cadence', { days: cadence })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          aria-expanded={editing}
          style={miniGhostButtonStyle}
        >
          {editing
            ? t.t('dashboard.journeys.card.closeButton')
            : t.t('dashboard.journeys.card.editButton')}
        </button>
      </div>

      <ToggleRow
        title={t.t('dashboard.journeys.card.enableTitle')}
        detail={t.t('dashboard.journeys.card.enableDetail')}
        on={journey.active}
        busy={toggling}
        disabled={toggling}
        onToggle={(next) => void setActive(next)}
        onLabel={t.t('dashboard.journeys.card.enableOn')}
        offLabel={t.t('dashboard.journeys.card.enableOff')}
        busyLabel={t.t('dashboard.journeys.card.enableBusy')}
      />
      {toggleError ? (
        <p
          role="alert"
          style={{
            margin: 'var(--space-2) 0 0 0',
            color: 'var(--color-accent)',
            fontSize: 'var(--text-meta)',
          }}
        >
          {toggleError}
        </p>
      ) : null}

      {editing ? (
        <JourneyEditor tenantId={tenantId} journey={journey} onClose={() => setEditing(false)} />
      ) : null}
    </article>
  );
}

// ── editor ───────────────────────────────────────────────────────────────────

type SaveStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'error'; readonly message: string };

function JourneyEditor({
  tenantId,
  journey,
  onClose,
}: {
  readonly tenantId: string;
  readonly journey: Journey;
  readonly onClose: () => void;
}) {
  const t = useI18n();
  const [form, setForm] = useState<JourneyForm>(() => formFromJourney(journey));
  const [activeLocale, setActiveLocale] = useState<Locale>(t.locale);
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });

  const isSegmentScan = journey.trigger.kind === 'segment_scan';

  function setStepField(i: number, patch: Partial<StepForm>) {
    setForm((f) => ({
      ...f,
      steps: f.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
    setStatus({ kind: 'idle' });
  }
  function setStepLocale(i: number, field: 'title' | 'body', value: string) {
    setForm((f) => ({
      ...f,
      steps: f.steps.map((s, idx) =>
        idx === i ? { ...s, [field]: { ...s[field], [activeLocale]: value } } : s,
      ),
    }));
    setStatus({ kind: 'idle' });
  }
  function setJourneyField(patch: Partial<JourneyForm>) {
    setForm((f) => ({ ...f, ...patch }));
    setStatus({ kind: 'idle' });
  }

  async function save() {
    setStatus({ kind: 'saving' });
    const result = buildJourneyUpdate(journey, form, Timestamp.now());
    if (!result.ok) {
      const message = t.t(
        `dashboard.journeys.editor.${result.errorKey}`,
        result.stepIndex !== undefined ? { step: result.stepIndex + 1 } : undefined,
      );
      setStatus({ kind: 'error', message });
      return;
    }
    try {
      await journeysRepoFor(tenantId).set(result.journey.id, result.journey);
      setStatus({ kind: 'saved' });
    } catch (e) {
      logger.error('journey save failed', { error: String(e), id: journey.id });
      setStatus({ kind: 'error', message: t.t('dashboard.journeys.editor.errSave') });
    }
  }

  const busy = status.kind === 'saving';

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--color-border)',
        display: 'grid',
        gap: 'var(--space-5)',
      }}
    >
      <div>
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.journeys.editor.heading', { name: journey.name })}
        </h3>
        <p style={{ margin: 'var(--space-1) 0 0 0', ...hintStyle, lineHeight: 1.5 }}>
          {t.t('dashboard.journeys.editor.subtitle')}
        </p>
      </div>

      <label style={fieldStack}>
        <span style={fieldLabel}>{t.t('dashboard.journeys.editor.nameLabel')}</span>
        <input
          value={form.name}
          maxLength={80}
          onChange={(e) => setJourneyField({ name: e.target.value })}
          style={inputStyle}
        />
      </label>

      {/* Language selector — one locale visible at a time, en always the fallback. */}
      <div style={fieldStack}>
        <span style={fieldLabel}>{t.t('dashboard.journeys.editor.localeLegend')}</span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {ALL_LOCALES.map((loc) => {
            const selected = loc === activeLocale;
            return (
              <button
                key={loc}
                type="button"
                aria-pressed={selected}
                onClick={() => setActiveLocale(loc)}
                style={chipStyle(selected)}
              >
                {LOCALE_NATIVE_NAMES[loc]}
                {loc === 'en' ? ` · ${t.t('dashboard.journeys.editor.localeRequired')}` : ''}
              </button>
            );
          })}
        </div>
        <span style={hintStyle}>{t.t('dashboard.journeys.editor.localeHint')}</span>
      </div>

      {/* Steps */}
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        {form.steps.map((step, i) => (
          <fieldset
            key={i}
            style={{
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
              margin: 0,
              display: 'grid',
              gap: 'var(--space-3)',
            }}
          >
            <legend style={{ ...fieldLabel, padding: '0 var(--space-2)' }}>
              {t.t('dashboard.journeys.editor.stepLabel', { n: i + 1 })}
            </legend>

            <div style={twoCol}>
              <label style={fieldStack}>
                <span style={fieldLabel}>{t.t('dashboard.journeys.editor.afterDaysLabel')}</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={step.afterDays}
                  onChange={(e) => setStepField(i, { afterDays: e.target.value })}
                  style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
                />
                <span style={hintStyle}>{t.t('dashboard.journeys.editor.afterDaysHint')}</span>
              </label>
              <label style={fieldStack}>
                <span style={fieldLabel}>{t.t('dashboard.journeys.editor.deepLinkLabel')}</span>
                <input
                  value={step.deepLink}
                  onChange={(e) => setStepField(i, { deepLink: e.target.value })}
                  placeholder={t.t('dashboard.journeys.editor.deepLinkPlaceholder')}
                  style={inputStyle}
                />
                <span style={hintStyle}>{t.t('dashboard.journeys.editor.deepLinkHint')}</span>
              </label>
            </div>

            <label style={fieldStack}>
              <span style={fieldLabel}>
                {t.t('dashboard.journeys.editor.titleLabel', {
                  locale: LOCALE_NATIVE_NAMES[activeLocale],
                })}
              </span>
              <input
                value={step.title[activeLocale] ?? ''}
                onChange={(e) => setStepLocale(i, 'title', e.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={fieldStack}>
              <span style={fieldLabel}>
                {t.t('dashboard.journeys.editor.bodyLabel', {
                  locale: LOCALE_NATIVE_NAMES[activeLocale],
                })}
              </span>
              <textarea
                value={step.body[activeLocale] ?? ''}
                onChange={(e) => setStepLocale(i, 'body', e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </label>
          </fieldset>
        ))}
      </div>

      {/* Delivery rules */}
      <div style={twoCol}>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t.t('dashboard.journeys.editor.freqCapLabel')}</span>
          <input
            type="number"
            min={1}
            max={14}
            step={1}
            inputMode="numeric"
            value={form.frequencyCapPerWeek}
            onChange={(e) => setJourneyField({ frequencyCapPerWeek: e.target.value })}
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
          <span style={hintStyle}>{t.t('dashboard.journeys.editor.freqCapHint')}</span>
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            paddingTop: 'var(--space-5)',
          }}
        >
          <input
            type="checkbox"
            checked={form.exitOnReturn}
            onChange={(e) => setJourneyField({ exitOnReturn: e.target.checked })}
            style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 2 }}
          />
          <span>
            <span style={{ ...fieldLabel, display: 'block' }}>
              {t.t('dashboard.journeys.editor.exitOnReturnLabel')}
            </span>
            <span style={hintStyle}>{t.t('dashboard.journeys.editor.exitOnReturnHint')}</span>
          </span>
        </label>
      </div>

      {/* Audience — segment_scan journeys only */}
      {isSegmentScan ? (
        <fieldset
          style={{
            border: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <legend style={fieldLabel}>{t.t('dashboard.journeys.editor.criteriaLegend')}</legend>
          <div style={twoCol}>
            <label style={fieldStack}>
              <span style={fieldLabel}>{t.t('dashboard.journeys.editor.inactiveDaysLabel')}</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={form.inactiveDays}
                onChange={(e) => setJourneyField({ inactiveDays: e.target.value })}
                placeholder="30"
                style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
              />
              <span style={hintStyle}>{t.t('dashboard.journeys.editor.inactiveDaysHint')}</span>
            </label>
            <label style={fieldStack}>
              <span style={fieldLabel}>{t.t('dashboard.journeys.editor.minVisitsLabel')}</span>
              <input
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.minVisits}
                onChange={(e) => setJourneyField({ minVisits: e.target.value })}
                placeholder="2"
                style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
              />
              <span style={hintStyle}>{t.t('dashboard.journeys.editor.minVisitsHint')}</span>
            </label>
          </div>
        </fieldset>
      ) : null}

      {status.kind === 'error' ? (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
        >
          {status.message}
        </p>
      ) : null}
      {status.kind === 'saved' ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {t.t('dashboard.journeys.editor.saved')}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          style={{
            ...primaryButtonStyle,
            opacity: busy ? 0.6 : 1,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? t.t('dashboard.journeys.editor.saving') : t.t('dashboard.journeys.editor.save')}
        </button>
        <button type="button" onClick={onClose} disabled={busy} style={ghostButtonStyle}>
          {t.t('dashboard.journeys.editor.cancel')}
        </button>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** Plain-language "what this journey does", from its trigger. */
function triggerSummary(journey: Journey, t: I18n): string {
  const trigger = journey.trigger;
  if (trigger.kind === 'segment_scan') {
    const { inactiveDays, minVisits } = trigger.criteria;
    if (inactiveDays !== undefined && minVisits !== undefined) {
      return t.t('dashboard.journeys.card.triggerSegmentScanFull', {
        days: inactiveDays,
        visits: minVisits,
      });
    }
    if (inactiveDays !== undefined) {
      return t.t('dashboard.journeys.card.triggerSegmentScanDays', { days: inactiveDays });
    }
    return t.t('dashboard.journeys.card.triggerSegmentScan');
  }
  if (trigger.kind === 'event') {
    return t.t('dashboard.journeys.card.triggerEvent');
  }
  return t.t('dashboard.journeys.card.triggerOperatorTrial', {
    days: trigger.daysBeforeTrialEnd.join(', '),
  });
}

// ── shared UI bits + styles (mirror /marketing + /marketing/segments) ──────────

function SkeletonRows() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 108,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-4) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

const fieldStack: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const hintStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const twoCol: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 'var(--space-3)',
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 12px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
  flexShrink: 0,
};
